import { useEffect, useMemo, useRef, useState } from 'react';
import { Camera, CheckCircle2, Package, ShieldCheck, ShieldX, Tag, Timer, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { BarcodeField } from './BarcodeField';
import { WorkPhotoCapture } from './WorkPhotoCapture';
import { useItems } from '@/features/inventory/hooks';
import { useBomComponents, useCompleteSerializedUnit } from '@/features/workMode/hooks';
import { compressImage } from '@/features/workMode/images';
import { describeWorkError, formatClock } from '@/features/workMode/api';
import { useComponentLotsOnHand, uploadUnitPhoto } from '@/features/traceability/hooks';
import { loadStickyLots, saveStickyLots } from '@/features/traceability/stickyLots';
import { SERIAL_MAX_PHOTOS, SERIAL_MIN_PHOTOS, type ComponentLot, type QcResult } from '@/features/traceability/types';
import type { WorkSession, WorkSessionSnapshot } from '@/features/workMode/types';

interface WorkSerialDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: WorkSession;
  stationId: string;
  itemName: string | null;
  /** Live net seconds on the unit, shown so the worker sees what gets recorded. */
  unitSeconds: number;
  onLogged?: (snapshot: WorkSessionSnapshot) => void;
}

/**
 * "+" for a serialized finished good. Scan the barcode, photograph the unit
 * (label close-up plus the product), confirm the component lots that went
 * into it, pass or fail QC, and the unit is logged with its cycle time.
 */
export function WorkSerialDialog({ open, onOpenChange, session, stationId, itemName, unitSeconds, onLogged }: WorkSerialDialogProps) {
  const [serial, setSerial] = useState('');
  const [serialError, setSerialError] = useState<string | null>(null);
  const [labelPhoto, setLabelPhoto] = useState<File | null>(null);
  const [photos, setPhotos] = useState<File[]>([]);
  const [lots, setLots] = useState<Record<string, string>>({});
  const [showAllParts, setShowAllParts] = useState(false);
  const [prefilled, setPrefilled] = useState(false);
  const [qc, setQc] = useState<QcResult>('pass');
  const [qcNotes, setQcNotes] = useState('');
  const [notes, setNotes] = useState('');
  const [noteOpen, setNoteOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const labelInputRef = useRef<HTMLInputElement | null>(null);

  const itemId = session.item_id;
  const { data: items = [] } = useItems();
  const { data: components = [] } = useBomComponents(open ? itemId : null);
  const complete = useCompleteSerializedUnit();

  const lotTracked = useMemo(() => new Set(items.filter((i) => i.is_lot_tracked).map((i) => i.id)), [items]);
  const trackedComponents = useMemo(() => components.filter((c) => lotTracked.has(c.item_id)), [components, lotTracked]);
  const otherComponents = useMemo(() => components.filter((c) => !lotTracked.has(c.item_id)), [components, lotTracked]);
  const visibleComponents = showAllParts || trackedComponents.length === 0 ? components : trackedComponents;
  const { data: lotsOnHand } = useComponentLotsOnHand(open ? visibleComponents.map((c) => c.item_id) : []);

  const labelUrl = useMemo(() => (labelPhoto ? URL.createObjectURL(labelPhoto) : null), [labelPhoto]);
  useEffect(() => {
    return () => {
      if (labelUrl) URL.revokeObjectURL(labelUrl);
    };
  }, [labelUrl]);

  // Reset per open; prefill lots from the last unit at this station + item.
  useEffect(() => {
    if (!open) return;
    setSerial('');
    setSerialError(null);
    setLabelPhoto(null);
    setPhotos([]);
    setShowAllParts(false);
    setQc('pass');
    setQcNotes('');
    setNotes('');
    setNoteOpen(false);
    const sticky = itemId ? loadStickyLots(stationId, itemId) : {};
    setLots(sticky);
    setPrefilled(Object.keys(sticky).length > 0);
  }, [open, stationId, itemId]);

  const photoCount = photos.length + (labelPhoto ? 1 : 0);
  const photosOk = photoCount >= SERIAL_MIN_PHOTOS;
  const filledLots = Object.values(lots).filter((v) => v.trim()).length;

  const submit = async () => {
    const s = serial.trim();
    if (!s) {
      setSerialError('Scan or type the serial number first.');
      return;
    }
    if (!photosOk) {
      toast.error(`Take at least ${SERIAL_MIN_PHOTOS} photos of the finished unit.`);
      return;
    }
    if (qc === 'fail' && !qcNotes.trim()) {
      toast.error('Say what failed QC so it can be fixed.');
      return;
    }
    setSerialError(null);
    try {
      setBusy('Uploading photos…');
      let labelPath: string | null = null;
      if (labelPhoto) {
        labelPath = await uploadUnitPhoto(session.organization_id, s, await compressImage(labelPhoto), 'label.jpg');
      }
      const paths: string[] = [];
      for (const [i, f] of photos.entries()) {
        const blob = await compressImage(f);
        paths.push(await uploadUnitPhoto(session.organization_id, s, blob, `unit-${i + 1}.jpg`));
      }
      setBusy('Logging unit…');
      const componentLots: ComponentLot[] = components
        .filter((c) => (lots[c.item_id] ?? '').trim())
        .map((c) => ({ item_id: c.item_id, sku: c.sku, name: c.name, lot_number: lots[c.item_id].trim(), lot_id: null }));
      const snap = await complete.mutateAsync({
        sessionId: session.id,
        serial: s,
        photoPaths: paths,
        labelPhotoPath: labelPath,
        componentLots,
        qcResult: qc,
        qcNotes: qc === 'fail' ? qcNotes.trim() : qcNotes.trim() || null,
        notes: notes.trim() || null,
      });
      if (itemId) saveStickyLots(stationId, itemId, lots);
      onLogged?.(snap);
      onOpenChange(false);
    } catch (e) {
      const msg = describeWorkError(e);
      if (String((e as Error)?.message ?? '').includes('work_serial')) setSerialError(msg);
      toast.error(msg);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-h-[94vh] max-w-3xl overflow-y-auto rounded-2xl p-0">
        <DialogHeader className="border-b border-border px-6 py-4">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Package className="h-5 w-5 text-bright-moss-dark" />
            Log unit{itemName ? ` · ${itemName}` : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 px-6 py-5">
          <section className="flex items-center gap-4 rounded-xl border border-border bg-secondary/40 px-4 py-3">
            <Timer className="h-5 w-5 shrink-0 text-muted-foreground" />
            <p className="min-w-0 flex-1 text-sm text-foreground">Build time recorded with this unit</p>
            <p className="text-3xl font-light tabular-nums tracking-tight text-foreground">{formatClock(unitSeconds)}</p>
          </section>

          <section className="space-y-2">
            <Step n={1} title="Serial number" done={!!serial.trim()} />
            <BarcodeField
              value={serial}
              onChange={(v) => {
                setSerial(v);
                if (serialError) setSerialError(null);
              }}
              autoFocus={open}
              disabled={!!busy}
            />
            {serialError && <p className="text-sm text-fresh-salmon-dark">{serialError}</p>}
          </section>

          <section className="space-y-3">
            <Step n={2} title="Photos" done={photosOk} hint={`${photoCount} of ${SERIAL_MIN_PHOTOS} needed`} />
            <div className="grid gap-4 sm:grid-cols-[auto_1fr]">
              <div className="space-y-1.5">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Serial label</p>
                {labelPhoto && labelUrl ? (
                  <div className="relative h-24 w-24 overflow-hidden rounded-xl border border-border bg-secondary">
                    <img src={labelUrl} alt="Serial label" className="h-full w-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setLabelPhoto(null)}
                      disabled={!!busy}
                      aria-label="Remove label photo"
                      className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full bg-humus/80 text-white hover:bg-humus"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={!!busy}
                    onClick={() => labelInputRef.current?.click()}
                    className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-border bg-card text-muted-foreground hover:bg-secondary disabled:opacity-50"
                  >
                    <Tag className="h-6 w-6" />
                    <span className="text-xs font-medium">Label</span>
                  </button>
                )}
                <input
                  ref={labelInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f && f.type.startsWith('image/')) setLabelPhoto(f);
                    e.target.value = '';
                  }}
                />
              </div>
              <div className="space-y-1.5">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Finished unit</p>
                <WorkPhotoCapture files={photos} onChange={setPhotos} max={SERIAL_MAX_PHOTOS - 1} disabled={!!busy} />
              </div>
            </div>
          </section>

          <section className="space-y-3">
            <Step
              n={3}
              title="Component lots"
              done={filledLots > 0}
              hint={components.length === 0 ? 'No BOM for this item' : `${filledLots} of ${visibleComponents.length} entered`}
              optional
            />
            {components.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                This item has no bill of materials yet, so there are no component lots to record.
              </p>
            ) : (
              <>
                {prefilled && (
                  <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <CheckCircle2 className="h-4 w-4 text-bright-moss-dark" />
                    Carried over from your last unit. Change any lot when you open a new box.
                  </p>
                )}
                <ul className="divide-y divide-border rounded-xl border border-border">
                  {visibleComponents.map((c) => {
                    const suggestions = (lotsOnHand?.get(c.item_id) ?? []).slice(0, 4);
                    const current = lots[c.item_id] ?? '';
                    return (
                      <li key={c.item_id} className="grid gap-2 px-3 py-2.5 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:items-center">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-foreground">{c.name}</p>
                          <p className="truncate font-mono text-[11px] text-muted-foreground">
                            {c.sku}
                            {lotTracked.has(c.item_id) ? ' · lot tracked' : ''}
                          </p>
                        </div>
                        <div className="space-y-1.5">
                          <Input
                            value={current}
                            disabled={!!busy}
                            placeholder="Lot number"
                            autoComplete="off"
                            className="h-11 font-mono"
                            onChange={(e) => setLots((l) => ({ ...l, [c.item_id]: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.preventDefault();
                            }}
                          />
                          {suggestions.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {suggestions.map((s) => (
                                <button
                                  key={s.lot_id}
                                  type="button"
                                  disabled={!!busy}
                                  onClick={() => setLots((l) => ({ ...l, [c.item_id]: s.lot_number }))}
                                  className={cn(
                                    'rounded-full border px-2.5 py-1 font-mono text-[11px] transition-colors',
                                    current.trim().toUpperCase() === s.lot_number.toUpperCase()
                                      ? 'border-info bg-info-soft/30 text-info'
                                      : 'border-border text-muted-foreground hover:bg-secondary',
                                  )}
                                  title={`${s.on_hand.toLocaleString()} on hand`}
                                >
                                  {s.lot_number}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
                {trackedComponents.length > 0 && otherComponents.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowAllParts((v) => !v)}
                    className="text-sm text-muted-foreground underline-offset-2 hover:underline"
                  >
                    {showAllParts
                      ? 'Show only lot-tracked parts'
                      : `Show all ${components.length} parts (${otherComponents.length} not lot tracked)`}
                  </button>
                )}
              </>
            )}
          </section>

          <section className="space-y-3">
            <Step n={4} title="Quality check" done />
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setQc('pass')}
                disabled={!!busy}
                className={cn(
                  'flex min-h-[64px] items-center justify-center gap-2 rounded-xl border px-3 py-2 text-base font-medium transition-colors',
                  qc === 'pass' ? 'border-bright-moss-dark bg-bright-moss/20 text-bright-moss-dark' : 'border-border bg-card text-foreground hover:bg-secondary',
                )}
              >
                <ShieldCheck className="h-5 w-5" />
                Passes QC
              </button>
              <button
                type="button"
                onClick={() => setQc('fail')}
                disabled={!!busy}
                className={cn(
                  'flex min-h-[64px] items-center justify-center gap-2 rounded-xl border px-3 py-2 text-base font-medium transition-colors',
                  qc === 'fail' ? 'border-fresh-salmon-dark bg-fresh-salmon/25 text-fresh-salmon-dark' : 'border-border bg-card text-foreground hover:bg-secondary',
                )}
              >
                <ShieldX className="h-5 w-5" />
                Fails · hold it
              </button>
            </div>
            {qc === 'fail' ? (
              <Textarea
                value={qcNotes}
                onChange={(e) => setQcNotes(e.target.value)}
                placeholder="What failed? The unit goes on hold until a manager releases it."
                className="min-h-[72px] text-base"
              />
            ) : (
              <Input
                value={qcNotes}
                onChange={(e) => setQcNotes(e.target.value)}
                placeholder="QC remarks (optional)"
                className="h-11"
              />
            )}
          </section>

          <section>
            {noteOpen ? (
              <div className="space-y-2">
                <Label className="text-base">Note</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything worth remembering about this unit." className="min-h-[72px] text-base" />
              </div>
            ) : (
              <button type="button" onClick={() => setNoteOpen(true)} className="text-sm text-muted-foreground underline-offset-2 hover:underline">
                Add a note about this unit
              </button>
            )}
          </section>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border px-6 py-4">
          <p className="hidden text-xs text-muted-foreground sm:block">
            <Camera className="mr-1 inline h-3.5 w-3.5" />
            Photos are compressed before upload.
          </p>
          <div className="ml-auto flex items-center gap-3">
            <Button variant="ghost" size="lg" onClick={() => onOpenChange(false)} disabled={!!busy}>
              Cancel
            </Button>
            <Button size="lg" onClick={submit} disabled={!!busy} className="min-w-40">
              {busy ?? (qc === 'fail' ? 'Log and hold' : 'Log unit')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Step({ n, title, done, hint, optional }: { n: number; title: string; done: boolean; hint?: string; optional?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={cn(
          'flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium',
          done ? 'bg-bright-moss text-humus-dark' : 'bg-secondary text-muted-foreground',
        )}
      >
        {n}
      </span>
      <Label className="text-base">{title}</Label>
      {optional && <span className="text-xs text-muted-foreground">optional</span>}
      {hint && <span className="ml-auto text-xs tabular-nums text-muted-foreground">{hint}</span>}
    </div>
  );
}
